import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import SearchModal from './components/SearchModal';
import { CONTACT_EMAIL } from './config';
import pushNotificationService from './services/pushNotifications';

const ConfiguracionView = () => {
  const navigate = useNavigate();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showClearCacheConfirm, setShowClearCacheConfirm] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setUnreadCount(pushNotificationService.getUnreadCount());

    const handler = () => setUnreadCount(pushNotificationService.getUnreadCount());
    window.addEventListener('notificationHistoryUpdated', handler);
    return () => window.removeEventListener('notificationHistoryUpdated', handler);
  }, []);

  // Vaciar caché
  const handleClearCache = () => {
    setShowClearCacheConfirm(true);
  };

  const confirmClearCache = () => {
    try {
      // Limpiar todo el localStorage de la app
      const allKeys = Object.keys(localStorage);
      
      allKeys.forEach(key => {
        if (key.startsWith('agustinos_')) {
          localStorage.removeItem(key);
        }
      });

      // Limpiar el caché del DataLoader si está disponible
      if (window.agustinosAppData?.loader) {
        window.agustinosAppData.loader.limpiarCache('articles');
        window.agustinosAppData.loader.limpiarCache('stats');
      }

      alert('✅ Caché eliminado correctamente. La aplicación se recargará.');
      
      // Recargar la página para aplicar cambios
      window.location.reload();
    } catch (error) {
      console.error('Error al limpiar caché:', error);
      alert('❌ Error al eliminar el caché. Inténtalo de nuevo.');
    }
    
    setShowClearCacheConfirm(false);
  };

  // Recomendar aplicación
  const handleRecommend = async () => {
    const shareData = {
      title: 'Agustinos App',
      text: '¡Descubre la app Agustinos! Contenido espiritual, reflexiones y mucho más.',
      url: 'https://www.appagustinos.es'
    };

    try {
      // Si estamos en app nativa (iOS/Android), usar Capacitor Share
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: shareData.title,
          text: shareData.text,
          url: shareData.url,
          dialogTitle: 'Recomendar aplicación'
        });
        console.log('✅ App compartida exitosamente (Capacitor)');
        return;
      }
      
      // Si estamos en web/PWA, usar Web Share API
      if (navigator.share) {
        await navigator.share(shareData);
        console.log('✅ App compartida exitosamente (Web Share API)');
      } else {
        // Fallback para navegadores que no soportan la API de compartir
        alert('Comparte esta app: https://www.appagustinos.es');
      }
    } catch (error) {
      // El usuario canceló o hubo un error
      const errorMessage = error.message || error.toString();
      const isCancelled = 
        error.name === 'AbortError' || 
        errorMessage.includes('cancel') ||
        errorMessage.includes('dismiss') ||
        errorMessage.includes('User cancelled');
      
      if (!isCancelled) {
        console.error('Error al compartir:', error);
      }
    }
  };

  // Contactar servicio técnico
  const handleContact = () => {
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=Consulta desde la app`;
  };

  // Navegar a créditos
  const handleCredits = () => {
    navigate('/creditos');
  };

  return (
    <div className="app-container">
      <div className="content-container">
        
        {/* ===== FIX iOS: Fondo extendido que cubre la safe area ===== */}
        <div 
          className="absolute inset-x-0 top-0"
          style={{
            background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)',
            height: 'calc(70px + env(safe-area-inset-top))',
            marginTop: 'calc(-1 * env(safe-area-inset-top))',
            zIndex: 1
          }}
        />
        
        {/* Header con navegación */}
        <div 
          className="relative text-white px-5"
          style={{
            paddingTop: 'max(env(safe-area-inset-top), 15px)',
            paddingBottom: '15px',
            minHeight: 'calc(70px + env(safe-area-inset-top))',
            zIndex: 10,
            background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)'
          }}
        >
          <div className="flex items-center justify-between" style={{ paddingTop: '15px' }}>
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white hover:bg-opacity-30 transition-all"
            >
              ←
            </button>
            
            <h1 className="text-lg font-semibold text-center flex-1 mx-4">
              Configuración
            </h1>
            
            <button
              onClick={() => setShowSearchModal(true)}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white hover:bg-opacity-30 transition-all"
            >
              🔍
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="bg-white" style={{ minHeight: '100vh' }}>
          
          {/* Sección: Información general */}
          <div className="px-5 pt-6 pb-2">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wide">
              Información general
            </h2>
          </div>

          <div className="bg-white">
            {/* Notificaciones */}
            <button
              onClick={() => navigate('/notificaciones')}
              className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">🔔</span>
                <span className="text-gray-800">Notificaciones</span>
                {unreadCount > 0 && (
                  <span 
                    className="text-xs text-white font-semibold rounded-full px-2 py-0.5 min-w-[20px] text-center"
                    style={{ backgroundColor: '#c82318' }}
                  >
                    {unreadCount}
                  </span>
                )}
              </div>
              <span className="text-gray-400 text-xl">›</span>
            </button>

            {/* Créditos */}
            <button
              onClick={handleCredits}
              className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="text-gray-800">Créditos</span>
              <span className="text-gray-400 text-xl">›</span>
            </button>

            {/* Contactar servicio técnico */}
            <button
              onClick={handleContact}
              className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="text-gray-800">Contactar con el servicio técnico</span>
              <span className="text-gray-400 text-xl">›</span>
            </button>

            {/* Recomendar aplicación */}
            <button
              onClick={handleRecommend}
              className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="text-gray-800">Recomendar aplicación</span>
              <span className="text-gray-400 text-xl">›</span>
            </button>
          </div>

          {/* Sección: Caché */}
          <div className="px-5 pt-8 pb-2">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wide">
              Caché
            </h2>
          </div>

          <div className="bg-white">
            {/* Vaciar caché */}
            <button
              onClick={handleClearCache}
              className="w-full flex items-center justify-between px-5 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="text-gray-800">Vaciar caché</span>
              <span className="text-gray-400 text-xl">›</span>
            </button>

            {/* Elimina el caché de la app */}
            <div className="px-5 py-4 border-b border-gray-200">
              <p className="text-gray-600 text-sm leading-relaxed">
                Elimina el caché de la app
              </p>
            </div>
          </div>

        </div>

        {/* Padding bottom para espacio */}
        <div style={{ height: '40px' }}></div>
      </div>

      {/* Modal de confirmación para limpiar caché */}
      {showClearCacheConfirm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
          onClick={() => setShowClearCacheConfirm(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              ¿Eliminar caché?
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Esto eliminará todos los datos almacenados localmente y la aplicación se recargará. 
              Tus preferencias de configuración se mantendrán.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearCacheConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-xl font-medium hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmClearCache}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de búsqueda */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        currentSection={null}
        onArticleClick={(article, section) => {
          const event = new CustomEvent('navigateToArticle', {
            detail: { article, section }
          });
          window.dispatchEvent(event);
        }}
      />
    </div>
  );
};

export default ConfiguracionView;

